/**
 * Forteresse ennemie d'une zone (phase D) : coque en boîtes de collision et emplacements de tourelles.
 * Les tourelles sont des ennemis fixes créés au début d'une mission « turrets ».
 */
import type { FortressData } from '../../data';
import { Quat, Vec3, UP } from '../core/math';
import type { WorldBox } from './dock';

export class Fortress {
  readonly position: Vec3;
  readonly rotation: Quat;

  constructor(readonly data: FortressData) {
    this.position = Vec3.from(data.position);
    this.rotation = Quat.from(data.rotation);
  }

  toWorld(local: readonly number[]): Vec3 {
    return this.position.add(this.rotation.rotate(Vec3.from(local)));
  }

  boxes(): WorldBox[] {
    return this.data.parts.map((part) => ({
      id: `fortress-${part.id}`,
      center: this.toWorld(part.center),
      rotation: this.rotation.clone(),
      half: Vec3.from(part.half),
    }));
  }

  /** Pose de départ d'une tourelle : sur son emplacement, canons vers l'extérieur de sa face d'appui. */
  turretPose(index: number): { position: Vec3; rotation: Quat } {
    const slot = this.data.turrets[index]!;
    const normal = this.rotation.rotate(Vec3.from(slot.normal)).normalized(UP());
    const up = Math.abs(normal.dot(UP())) > 0.9 ? this.rotation.forward().neg() : UP();
    return { position: this.toWorld(slot.position), rotation: Quat.lookRotation(normal, up) };
  }

  /** Normale (monde) de la face d'appui de l'emplacement de tourelle situé en `position`, ou null. */
  mountNormal(position: Vec3): Vec3 | null {
    for (const slot of this.data.turrets) {
      if (this.toWorld(slot.position).sub(position).lengthSq() < 1) return this.rotation.rotate(Vec3.from(slot.normal)).normalized(UP());
    }
    return null;
  }
}
